import { inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, exhaustMap, filter, interval, map, merge, pipe, switchMap, tap, timer } from 'rxjs';
import { GameStatus, GamesTypes } from '@app/domain/enums/game';
import type { Game } from '@app/domain/interfaces/game/game.interface';
import { GameService } from '@app/services/game/game';

export interface CountdownBarItem {
  id: number;
  game: Game;
  type: GamesTypes;
  startAtMs: number;
  remainingSeconds: number;
  state: CountdownBarState;
  urgent: boolean;
}

export type CountdownBarState = 'countdown' | 'live';

const FIVE_MINUTES_MS = 300_000;
const LIVE_VISIBLE_MS = 10_000;
const URGENT_THRESHOLD_MS = 5_000;
const TICK_MS = 1_000;
const REFRESH_MS = 15_000;
const MS_PER_SECOND = 1_000;
const NO_START = 0;
const LIVE_FIRST = -1;
const LIVE_SECOND = 1;

const BLOCKED_ROUTE_PREFIXES = ['/live-game', '/buy-cards', '/goal-rush'];
const SUPPORTED_TYPES = new Set<GamesTypes>([
  GamesTypes.BIG,
  GamesTypes.BINGOALS,
  GamesTypes.CRICKET,
  GamesTypes.DARTS,
  GamesTypes.FOOTBALL,
  GamesTypes.GOAL_RUSH,
  GamesTypes.NOW,
  GamesTypes.QUICK,
  GamesTypes.SOCCER,
  GamesTypes.TENNIS,
]);

function compareItems(first: CountdownBarItem, second: CountdownBarItem): number {
  if (first.state !== second.state) {
    return first.state === 'live' ? LIVE_FIRST : LIVE_SECOND;
  }

  return first.startAtMs - second.startAtMs;
}

function getStartAtMs(value: string, now: number): null | number {
  const utcTime = parseDate(value, true);
  const localTime = parseDate(value, false);
  const candidates = [utcTime, localTime]
    .filter((time): time is number => time !== null)
    .filter((time, index, arr) => arr.indexOf(time) === index);
  const visible = candidates.find(time => {
    const diff = time - now;

    return diff >= -LIVE_VISIBLE_MS && diff <= FIVE_MINUTES_MS;
  });

  return visible ?? utcTime ?? localTime;
}

function parseDate(value: string, asUtc: boolean): null | number {
  if (!value) {
    return null;
  }

  const time = new Date(toIso(value, asUtc)).getTime();

  return Number.isNaN(time) ? null : time;
}

function toIso(value: string, asUtc: boolean): string {
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');

  if (asUtc && !normalized.endsWith('Z')) {
    return `${normalized}Z`;
  }

  return normalized;
}

export const countdownBarStore = signalStore(
  withState(() => ({
    collapsed: false,
    items: [] as CountdownBarItem[],
  })),
  withProps(() => ({
    gameService: inject(GameService),
    router: inject(Router),
  })),
  withMethods(store => {
    let games: Game[] = [];
    let knownVisibleIds = new Set<number>;
    const liveUntilById = new Map<number, number>;
    const dismissedLiveIds = new Set<number>;

    const expand = (): void => {
      patchState(store, { collapsed: false });
    };

    const getLiveUntil = (id: number, diff: number, now: number): null | number => {
      if (dismissedLiveIds.has(id)) {
        return null;
      }

      const existing = liveUntilById.get(id);

      if (existing !== undefined) {
        return existing;
      }

      if (diff <= NO_START && Math.abs(diff) <= LIVE_VISIBLE_MS) {
        const liveUntil = now + LIVE_VISIBLE_MS;

        liveUntilById.set(id, liveUntil);

        return liveUntil;
      }

      return null;
    };

    const countdownItem = (game: Game, startAtMs: number, diff: number): CountdownBarItem | null => {
      const runningStatus: string = GameStatus.RUNNING;
      const isRunning = game.status.toLowerCase() === runningStatus;

      if (isRunning || diff < NO_START || diff > FIVE_MINUTES_MS) {
        return null;
      }

      return {
        id: game.id,
        game,
        type: game.type,
        startAtMs,
        remainingSeconds: Math.max(NO_START, Math.floor(diff / MS_PER_SECOND)),
        state: 'countdown',
        urgent: diff <= URGENT_THRESHOLD_MS,
      };
    };

    const resolveItem = (game: Game, startAtMs: number, now: number): CountdownBarItem | null => {
      const diff = startAtMs - now;
      const liveUntil = getLiveUntil(game.id, diff, now);

      if (liveUntil !== null && liveUntil > now) {
        return { id: game.id,
          game,
          type: game.type,
          startAtMs,
          remainingSeconds: NO_START,
          state: 'live',
          urgent: false };
      }

      if (liveUntil !== null) {
        dismissedLiveIds.add(game.id);
        liveUntilById.delete(game.id);
      }

      return countdownItem(game, startAtMs, diff);
    };

    const classify = (game: Game, now: number): CountdownBarItem | null => {
      if (!game.id || !SUPPORTED_TYPES.has(game.type)) {
        return null;
      }

      const startAtMs = getStartAtMs(game.run_at, now);

      if (startAtMs === null) {
        return null;
      }

      return resolveItem(game, startAtMs, now);
    };

    const openWhenNewItemAppears = (items: CountdownBarItem[]): void => {
      const hasNewItem = items.some(item => !knownVisibleIds.has(item.id));

      if (hasNewItem) {
        expand();
      }

      knownVisibleIds = new Set(items.map(item => item.id));
    };

    const buildItems = (): void => {
      const now = Date.now();
      const items = games
        .map(game => classify(game, now))
        .filter((item): item is CountdownBarItem => item !== null)
        .sort(compareItems);

      openWhenNewItemAppears(items);
      patchState(store, { items });
    };

    return {
      expand,
      collapse(): void {
        patchState(store, { collapsed: true });
      },
      isRouteAllowed(): boolean {
        const path = store.router.url.split('?')[0];

        return !BLOCKED_ROUTE_PREFIXES.some(prefix => path.startsWith(prefix));
      },
      loadGames: rxMethod<undefined>(pipe(
        exhaustMap(() => store.gameService.getUserLiveAndPendingGames$().pipe(
          tap(fetched => {
            games = fetched;
            buildItems();
          }),
          catchError(() => {
            games = [];
            buildItems();

            return EMPTY;
          }),
        )),
      )),
      tick: rxMethod<undefined>(pipe(
        switchMap(() => interval(TICK_MS)),
        tap(() => {
          buildItems();
        }),
      )),
    };
  }),
  withHooks({
    onInit(store) {
      const refresh$ = merge(
        timer(NO_START, REFRESH_MS),
        store.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)),
      ).pipe(map(() => undefined));

      store.loadGames(refresh$);
      store.tick(undefined);
    },
  }),
);
